import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Package, MoreVertical, Edit, Trash2, Percent, DollarSign, Plus } from "lucide-react";
import {
  useProductBundles,
  useDeleteProductBundle,
  calculateBundleSavings,
  ProductBundle,
} from "@/hooks/useProductBundles";
import { AddBundleDialog } from "./AddBundleDialog";
import { EditBundleDialog } from "./EditBundleDialog";

function formatCurrency(amount: number, currency: string = "USD") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).format(amount);
}

export function ProductBundlesManager() {
  const { data: bundles, isLoading } = useProductBundles();
  const deleteBundle = useDeleteProductBundle();
  const [editingBundle, setEditingBundle] = useState<ProductBundle | null>(null);
  const [deletingBundle, setDeletingBundle] = useState<ProductBundle | null>(null);

  const handleDelete = async () => {
    if (deletingBundle) {
      await deleteBundle.mutateAsync(deletingBundle.id);
      setDeletingBundle(null);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-32" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Product Bundles</h3>
          <p className="text-sm text-muted-foreground">
            Create bundles to offer grouped products at discounted prices
          </p>
        </div>
        <AddBundleDialog />
      </div>

      {!bundles?.length ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Package className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold">No bundles yet</h3>
            <p className="text-muted-foreground mb-4">
              Create your first bundle to offer grouped products
            </p>
            <AddBundleDialog />
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {bundles.map((bundle) => {
            const savings = calculateBundleSavings(bundle);
            return (
              <Card key={bundle.id} className="group relative">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{bundle.name}</CardTitle>
                      {bundle.description && (
                        <CardDescription className="mt-1">
                          {bundle.description}
                        </CardDescription>
                      )}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="opacity-0 group-hover:opacity-100"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setEditingBundle(bundle)}>
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => setDeletingBundle(bundle)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant={bundle.is_active ? "default" : "secondary"}>
                      {bundle.is_active ? "Active" : "Inactive"}
                    </Badge>
                    {savings.savingsPercent > 0 && (
                      <Badge className="bg-green-500/10 text-green-500">
                        Save {savings.savingsPercent.toFixed(0)}%
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Products in bundle */}
                  <div className="space-y-2 mb-4">
                    <p className="text-xs font-medium text-muted-foreground uppercase">
                      Included Products ({bundle.items?.length || 0})
                    </p>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {bundle.items?.map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center justify-between text-sm py-1"
                        >
                          <span className="truncate">{item.product?.name || "Unknown"}</span>
                          <span className="text-muted-foreground">×{item.quantity}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Pricing */}
                  <div className="border-t pt-3 space-y-2">
                    {savings.originalPrice > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Original Price</span>
                        <span className="line-through text-muted-foreground">
                          {formatCurrency(savings.originalPrice, bundle.currency)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="font-medium">Bundle Price</span>
                      <span className="font-bold text-primary">
                        {formatCurrency(bundle.bundle_price, bundle.currency)}
                      </span>
                    </div>
                    {savings.savings > 0 && (
                      <div className="flex justify-between text-sm text-green-500">
                        <span>You Save</span>
                        <span>{formatCurrency(savings.savings, bundle.currency)}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {editingBundle && (
        <EditBundleDialog
          bundle={editingBundle}
          open={!!editingBundle}
          onOpenChange={(open) => !open && setEditingBundle(null)}
        />
      )}

      <AlertDialog open={!!deletingBundle} onOpenChange={(open) => !open && setDeletingBundle(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Bundle</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deletingBundle?.name}"? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
